// src/modals/EventFormModal.js
// Modal partagée création / édition d'événement

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, MapPin, X, AlertCircle, Globe, ImageIcon } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { useModal } from '../hooks';
import eventService from '../services/eventService';
import { EVENT_CATEGORIES } from '../constants';
import { Btn, Select } from '../components/ui';
import { searchAddresses } from '../utils/geocoding';
import PajNtaubBand from '../components/ui/PajNtaubBand';

const toLocalDatetime = (ts) => {
  if (!ts) return '';
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EventFormModal = ({ isOpen, onClose, user, toast, event, onSaved }) => {
  const { t } = useTranslation();
  const isEdit = !!event;
  const { onBackdrop } = useModal(isOpen, onClose);
  const fileRef = useRef(null);
  const debounceRef = useRef(null);

  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    date: '',
    endDate: '',
    online: false,
    locationName: '',
    locationAddress: '',
    locationCity: '',
    locationPostalCode: '',
    locationLat: null,
    locationLng: null,
    maxAttendees: '',
    price: '0',
    imageFile: null,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [locQuery, setLocQuery] = useState('');
  const [locSuggestions, setLocSuggestions] = useState([]);
  const [showLocDropdown, setShowLocDropdown] = useState(false);

  // Pré-remplir en mode édition
  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && event) {
      setForm({
        title: event.title || '',
        description: event.description || '',
        category: event.category || '',
        date: toLocalDatetime(event.date),
        endDate: toLocalDatetime(event.endDate),
        online: event.online || false,
        locationName: event.location?.name || '',
        locationAddress: event.location?.address || '',
        locationCity: event.location?.city || '',
        locationPostalCode: event.location?.postalCode || '',
        locationLat: event.location?.lat || null,
        locationLng: event.location?.lng || null,
        maxAttendees:
          event.maxAttendees !== null && event.maxAttendees !== undefined
            ? String(event.maxAttendees)
            : '',
        price: event.price !== null && event.price !== undefined ? String(event.price) : '0',
        imageFile: null,
      });
      setPreview(event.imageUrl || null);
      const loc = event.location;
      if (loc && loc.address) {
        setLocQuery(loc.address);
      } else if (loc && loc.name) {
        setLocQuery(loc.name);
      }
    } else {
      setForm({
        title: '',
        description: '',
        category: '',
        date: '',
        endDate: '',
        online: false,
        locationName: '',
        locationAddress: '',
        locationCity: '',
        locationPostalCode: '',
        locationLat: null,
        locationLng: null,
        maxAttendees: '',
        price: '0',
        imageFile: null,
      });
      setPreview(null);
      setLocQuery('');
    }
    setErrors({});
    setLocSuggestions([]);
    setShowLocDropdown(false);
  }, [isOpen, isEdit, event]);

  // Autocomplete adresse (debounce 300ms)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (locQuery.length < 3 || form.online) {
      setLocSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const results = await searchAddresses(locQuery);
      setLocSuggestions(results);
      setShowLocDropdown(results.length > 0);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [locQuery, form.online]);

  const selectLocation = (loc) => {
    setLocQuery(loc.address);
    setShowLocDropdown(false);
    setLocSuggestions([]);
    setForm((prev) => ({
      ...prev,
      locationName: loc.name,
      locationAddress: loc.address,
      locationCity: loc.city,
      locationPostalCode: loc.postcode,
      locationLat: loc.lat,
      locationLng: loc.lng,
    }));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, image: t('eventForm.validation.image') }));
      return;
    }
    setPreview(URL.createObjectURL(file));
    setForm((prev) => ({ ...prev, imageFile: file }));
    setErrors((prev) => ({ ...prev, image: null }));
  };

  const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const validate = () => {
    const errs = {};
    if (!form.title.trim() || form.title.trim().length < 3)
      errs.title = t('eventForm.validation.titleMin');
    if (form.title.trim().length > 100) errs.title = t('eventForm.validation.titleMax');
    if (!form.description.trim() || form.description.trim().length < 10)
      errs.description = t('eventForm.validation.descMin');
    if (form.description.trim().length > 1000) errs.description = t('eventForm.validation.descMax');
    if (!form.category) errs.category = t('eventForm.validation.category');
    if (!form.date) errs.date = t('eventForm.validation.date');
    if (form.date && new Date(form.date) < new Date())
      errs.date = t('eventForm.validation.dateFuture');
    if (form.endDate && form.date && new Date(form.endDate) < new Date(form.date))
      errs.endDate = t('eventForm.validation.endDate');
    if (!form.online && !form.locationAddress) errs.location = t('eventForm.validation.location');
    if (form.maxAttendees && (isNaN(Number(form.maxAttendees)) || Number(form.maxAttendees) < 1))
      errs.maxAttendees = t('eventForm.validation.maxAttendees');
    if (form.price === '' || isNaN(Number(form.price)) || Number(form.price) < 0)
      errs.price = t('eventForm.validation.price');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      category: form.category,
      date: Timestamp.fromDate(new Date(form.date)),
      endDate: form.endDate ? Timestamp.fromDate(new Date(form.endDate)) : null,
      online: form.online,
      location: form.online
        ? { name: 'En ligne', address: '', city: '', postalCode: '', lat: null, lng: null }
        : {
            name: form.locationName,
            address: form.locationAddress,
            city: form.locationCity,
            postalCode: form.locationPostalCode,
            lat: form.locationLat,
            lng: form.locationLng,
          },
      maxAttendees: form.maxAttendees ? Number(form.maxAttendees) : null,
      price: Number(form.price),
      ...(form.imageFile && { imageFile: form.imageFile }),
      // En édition : si la photo a été supprimée (pas de nouveau fichier + preview vidé), envoyer imageUrl: ''
      ...(isEdit && !form.imageFile && !preview && event?.imageUrl ? { imageUrl: '' } : {}),
    };

    let result;
    if (isEdit) {
      result = await eventService.updateEvent(event.id, payload, user.uid);
    } else {
      result = await eventService.createEvent({
        ...payload,
        organizerId: user.uid,
        organizerName: user.name || '',
        organizerPhoto: user.profilePicture || '',
      });
    }

    setLoading(false);

    if (result.success) {
      toast(isEdit ? t('eventForm.toastEdited') : t('eventForm.toastCreated'), 'success');
      onClose();
      // Petit délai pour laisser Firestore propager les index composites
      setTimeout(() => onSaved?.(), 300);
    } else {
      toast(result.error || t('eventForm.toastError'), 'error');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 animate-fade-in"
      onClick={onBackdrop}
    >
      <div
        className="relative w-full sm:max-w-lg bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-xl shadow-xl max-h-[85dvh] overflow-y-auto animate-slide-up"
        style={{ overscrollBehavior: 'contain' }}
        data-testid="event-form-modal"
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? t('eventForm.titleEdit') : t('eventForm.titleCreate')}
      >
        <PajNtaubBand height={4} />
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-semibold dark:text-gray-100">
              {isEdit ? t('eventForm.titleEdit') : t('eventForm.titleCreate')}
            </h3>
            <button
              onClick={onClose}
              aria-label={t('eventForm.ariaClose')}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X size={20} />
            </button>
          </div>

          <div className="text-center mb-5">
            <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full mx-auto flex items-center justify-center text-white mb-2">
              <Calendar size={24} />
            </div>
          </div>

          {/* Erreur globale */}
          {errors.global && (
            <div
              className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 rounded-xl text-sm flex items-center gap-2"
              data-testid="event-form-error"
            >
              <AlertCircle size={16} />
              {errors.global}
            </div>
          )}

          <div className="space-y-4">
            {/* Titre */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('eventForm.titleLabel')} <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder={t('eventForm.titlePlaceholder')}
                maxLength={100}
                className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 ${
                  errors.title
                    ? 'border-red-300'
                    : 'border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500'
                } rounded-xl dark:text-gray-100 focus:outline-none transition`}
              />
              {errors.title && <p className="text-red-500 text-sm">{errors.title}</p>}
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('eventForm.descLabel')} <span className="text-rose-500">*</span>
              </label>
              <textarea
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder={t('eventForm.descPlaceholder')}
                maxLength={1000}
                rows={3}
                className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 ${
                  errors.description
                    ? 'border-red-300'
                    : 'border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500'
                } rounded-xl dark:text-gray-100 focus:outline-none transition resize-none`}
              />
              <div className="flex justify-between">
                {errors.description && <p className="text-red-500 text-sm">{errors.description}</p>}
                <p className="text-gray-400 text-xs ml-auto">{form.description.length}/1000</p>
              </div>
            </div>

            {/* Catégorie */}
            <Select
              label={t('eventForm.categoryLabel')}
              required
              options={EVENT_CATEGORIES.map((c) => ({ value: c.value, label: t(c.labelKey) }))}
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
              placeholder={t('eventForm.categoryPlaceholder')}
            />
            {errors.category && <p className="text-red-500 text-sm -mt-2">{errors.category}</p>}

            {/* Date & heure */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('eventForm.dateLabel')} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={form.date}
                  onChange={(e) => set('date', e.target.value)}
                  className={`w-full px-3 py-3 bg-gray-50 dark:bg-gray-800 border-2 ${
                    errors.date
                      ? 'border-red-300'
                      : 'border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500'
                  } rounded-xl dark:text-gray-100 focus:outline-none transition text-sm`}
                />
                {errors.date && <p className="text-red-500 text-xs">{errors.date}</p>}
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('eventForm.endDateLabel')}
                </label>
                <input
                  type="datetime-local"
                  value={form.endDate}
                  onChange={(e) => set('endDate', e.target.value)}
                  className={`w-full px-3 py-3 bg-gray-50 dark:bg-gray-800 border-2 ${
                    errors.endDate
                      ? 'border-red-300'
                      : 'border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500'
                  } rounded-xl dark:text-gray-100 focus:outline-none transition text-sm`}
                />
                {errors.endDate && <p className="text-red-500 text-xs">{errors.endDate}</p>}
              </div>
            </div>

            {/* En ligne toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.online}
                onChange={(e) => set('online', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-rose-500 focus:ring-rose-500"
              />
              <Globe size={18} className="text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('eventForm.onlineLabel')}
              </span>
            </label>

            {/* Lieu (autocomplete) — masqué si online */}
            {!form.online && (
              <div className="relative space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('eventForm.locationLabel')} <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                    <MapPin size={18} />
                  </div>
                  <input
                    type="text"
                    value={locQuery}
                    onChange={(e) => {
                      setLocQuery(e.target.value);
                      if (form.locationAddress) {
                        setForm((prev) => ({
                          ...prev,
                          locationName: '',
                          locationAddress: '',
                          locationCity: '',
                          locationPostalCode: '',
                          locationLat: null,
                          locationLng: null,
                        }));
                      }
                    }}
                    placeholder={t('eventForm.locationPlaceholder')}
                    className={`w-full pl-11 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 ${
                      errors.location
                        ? 'border-red-300'
                        : 'border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500'
                    } rounded-xl dark:text-gray-100 focus:outline-none transition`}
                  />
                </div>

                {showLocDropdown && locSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden max-h-48 overflow-y-auto">
                    {locSuggestions.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => selectLocation(s)}
                        className="w-full px-4 py-3 text-left hover:bg-rose-50 dark:hover:bg-gray-800 transition flex items-center gap-2 text-sm dark:text-gray-200"
                      >
                        <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                        <span>{s.address}</span>
                      </button>
                    ))}
                  </div>
                )}

                {errors.location && <p className="text-red-500 text-sm">{errors.location}</p>}

                {form.locationAddress && (
                  <div className="mt-1 p-2.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl flex items-center gap-2">
                    <MapPin
                      size={14}
                      className="text-emerald-600 dark:text-emerald-400 flex-shrink-0"
                    />
                    <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                      {form.locationAddress}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Photo de couverture */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('eventForm.coverLabel')}
              </label>
              {preview ? (
                <div className="relative">
                  <img
                    src={preview}
                    alt="Preview"
                    className="w-full h-36 object-cover rounded-xl"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPreview(null);
                      set('imageFile', null);
                    }}
                    className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1 hover:bg-black/70 transition"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full h-28 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl flex flex-col items-center justify-center gap-2 text-gray-400 hover:border-rose-300 dark:hover:border-rose-500 hover:text-rose-400 transition"
                >
                  <ImageIcon size={24} />
                  <span className="text-xs">{t('eventForm.coverHint')}</span>
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFile}
                className="hidden"
              />
              {errors.image && <p className="text-red-500 text-sm">{errors.image}</p>}
            </div>

            {/* Places max + Prix */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('eventForm.maxAttendeesLabel')}
                </label>
                <input
                  type="number"
                  value={form.maxAttendees}
                  onChange={(e) => set('maxAttendees', e.target.value)}
                  placeholder={t('eventForm.maxAttendeesPlaceholder')}
                  min="1"
                  className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 ${
                    errors.maxAttendees
                      ? 'border-red-300'
                      : 'border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500'
                  } rounded-xl dark:text-gray-100 focus:outline-none transition`}
                />
                {errors.maxAttendees && (
                  <p className="text-red-500 text-xs">{errors.maxAttendees}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('eventForm.priceLabel')} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="number"
                  value={form.price}
                  onChange={(e) => set('price', e.target.value)}
                  min="0"
                  step="0.01"
                  className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 ${
                    errors.price
                      ? 'border-red-300'
                      : 'border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500'
                  } rounded-xl dark:text-gray-100 focus:outline-none transition`}
                />
                {errors.price && <p className="text-red-500 text-xs">{errors.price}</p>}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <Btn
              v="ghost"
              onClick={onClose}
              className="flex-1 text-sm py-2.5"
              data-testid="event-form-cancel"
            >
              {t('eventForm.cancelBtn')}
            </Btn>
            <Btn
              onClick={handleSubmit}
              loading={loading}
              className="flex-1 text-sm py-2.5"
              data-testid="event-form-submit"
            >
              {isEdit ? t('eventForm.submitEdit') : t('eventForm.submitCreate')}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventFormModal;
